///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { isVideoRenderMode } from "@/earthbrowser/utils/renderMode"
import { logger } from "@/utils/LogDispatcher"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { trig } from "@stovokor/math/math"
import { DatasetLayer, type ArrowRow, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { CutoutElevation } from "./cutout/CutoutElevation"
import { buildLayout, fromBlockXZ, insideFootprint, localOffsetKm, sequenceTrend, toBlockXZ, type CutoutLayout } from "./cutout/cutoutLayout"
import { QuakeCutoutNode } from "./cutout/QuakeCutoutNode"
import { QuakeHistoryQuery } from "./cutout/QuakeHistoryQuery"
import { UsgsDetail } from "./cutout/UsgsDetail"
import { Slab2Surface } from "./cutout/Slab2Query"
import { ExposureQuery } from "./cutout/ExposureQuery"
import type { QuakeCutoutChart, QuakeCutoutSummary, QuakeCutoutUpdate } from "@/redux/dialogSlice"

/** Aftershock magnitude vs. hours-after-mainshock points for the HUD chart */
function chartData(sequence: QuakeSequence, layout: CutoutLayout): QuakeCutoutChart {
	const main = sequence.mainshock
	const mainshockTimeMs = Date.parse(main.event_time)
	const points: Array<[number, number, number]> = []
	let maxHours = 24 * 7
	let magMin = main.mag
	for (const e of sequence.events) {
		if (!e.inSequence || e.id === main.id) continue
		const hours = (e.timeMs - mainshockTimeMs) / 3.6e6
		if (hours <= 0) continue
		points.push([Math.round(hours * 1000) / 1000, e.mag, Math.round(e.depthKm * 10) / 10])
		maxHours = Math.max(maxHours, hours)
		magMin = Math.min(magMin, e.mag)
	}
	return {
		mainshockTimeMs,
		mainshockMag: main.mag,
		points,
		depthFloorKm: layout.depthFloorKm,
		maxHours,
		magMin: Math.floor(magMin),
		magMax: Math.ceil(main.mag + 0.4)
	}
}
import { QuakeSequenceQuery, type QuakeSequence } from "./cutout/QuakeSequenceQuery"
import { EarthquakesTable, type EarthquakeProperties } from "./EarthquakesTable"
import { QuakeFeature, type QuakePickCandidate } from "./QuakeFeature"

// Pointer arbitration across a cluster of overlapping domes. A pointer
// within this fraction of a dome's radius counts as deliberate aim at that
// quake; otherwise the largest quake under the pointer wins
const cPickAimThreshold = 0.35
// The current pick stays while the pointer is within this multiple of its
// radius, so the hover card doesn't flicker along overlap boundaries
const cPickStickyInflate = 1.3

/** Shape of an earthquakes Arrow row (only fields we read) */
type EarthquakeRow = {
	id: string
	place: string
	title: string
	lon: number
	lat: number
	depth_km: number
	mag: number
	mag_type: string | null
	type: string
	event_time: string
	updated: string
	ingested_at: string
	dmin: number | null | undefined
	rms: number | null | undefined
	gap: number | null | undefined
	felt: number | null | undefined
	cdi: number | null | undefined
	mmi: number | null | undefined
	alert: string | null
	tsunami: number | null
	sig: number | null | undefined
	status: string
	net: string
	code: string
	ids: string
	sources: string
	types: string
	nst: number | null | undefined
	url: string
	detail: string
}

export class EarthquakesDatasetLayer extends DatasetLayer<EarthquakeProperties> {
	private unsubscribeRedux?: () => void

	/** Open cutout block (at most one) and the quake it belongs to */
	private cutout?: QuakeCutoutNode
	private cutoutQuakeId?: string
	private cutoutElevation?: CutoutElevation
	/** Bumped on every open/close so stale async steps bail out */
	private cutoutToken = 0

	/** Live instance for the dev debug hooks */
	public static live?: EarthquakesDatasetLayer

	constructor(context: EarthBrowserContext) {
		super(context, "earthquakes")
		EarthquakesDatasetLayer.live = this
		this.subscribeToRedux()
	}

	private subscribeToRedux(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("earthquake") ?? false
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			const isVisible = state?.earthbrowser.visibleDatasets.includes("earthquake") ?? false
			if (isVisible !== this.visible) {
				this.visible = isVisible
				if (!isVisible && this.cutoutQuakeId) {
					// Layer switched off under an open cutout: tear it down and
					// hand the camera back to Earth where it stands
					this.closeCutout(false)
					engineBridge.dispatch({ type: engineActions.setQuakeCutout, payload: null })
				}
			}

			const wantId = state?.dialog.quakeCutout?.quakeId
			if (wantId !== this.cutoutQuakeId) {
				if (this.cutoutQuakeId) {
					this.closeCutout(wantId === undefined)
				}
				if (wantId) {
					void this.openCutout(wantId)
				}
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		this.closeCutout(false)
		if (EarthquakesDatasetLayer.live === this) {
			EarthquakesDatasetLayer.live = undefined
		}
		super.destroy()
	}

	// ==================== Pointer picking ====================

	/** Quake the pointer last resolved to, kept sticky near its rim */
	private lastPick?: QuakeFeature

	/**
	 * The engine ranks hits by ray distance, which for a swarm of overlapping
	 * domes is noise: a big dome grazed at its rim sits above a small quake
	 * dead under the cursor, and same-size flecks race on sub-pixel
	 * differences. Hover also fires every hit's recognizer, so the hover
	 * card and the tap could land on different quakes. The layer resolves
	 * the cluster to one quake itself and reports only that hit, so hover
	 * and tap agree by construction. Other children (the cutout block) hit
	 * test normally
	 */
	override gestureHitTest(state: RenderState): GestureHitResult[] {
		if (!this.visible) {
			return []
		}
		state.pushRay(state.ray.multMatrix(this._transform.inverse))

		const hits: GestureHitResult[] = []
		const candidates: QuakePickCandidate[] = []
		const pointer = state.ray
		const cameraPosition = state.camera.position
		for (const child of this.children) {
			if (child instanceof QuakeFeature) {
				const candidate = child.pickCandidate(pointer, cameraPosition)
				if (candidate) candidates.push(candidate)
			} else {
				hits.push(...child.gestureHitTest(state))
			}
		}

		let pick = this.resolvePick(candidates)
		// Hysteresis: hold the previous pick while the pointer is still near
		// it, unless the new pick is a deliberate aim at a different quake
		const last = this.lastPick
		if (last && pick?.feature !== last && (!pick || pick.offset >= cPickAimThreshold)) {
			const held = this.sceneObjects.get(last.id) === last
				? last.pickCandidate(pointer, cameraPosition, cPickStickyInflate)
				: undefined
			if (held) pick = held
		}
		this.lastPick = pick?.feature
		if (pick) hits.push(pick.hit)

		state.popRay()
		return hits
	}

	/**
	 * Deliberate aim wins: the best-centered quake whose center the pointer
	 * is close to, so a small aftershock stays selectable inside a big dome.
	 * Otherwise a casual hover over the cluster picks the largest quake,
	 * mirroring the mainshock rule the cutout uses
	 */
	private resolvePick(candidates: QuakePickCandidate[]): QuakePickCandidate | undefined {
		let aimed: QuakePickCandidate | undefined
		let largest: QuakePickCandidate | undefined
		for (const c of candidates) {
			if (c.offset < cPickAimThreshold && (!aimed || c.offset < aimed.offset)) {
				aimed = c
			}
			if (!largest || c.mag > largest.mag || (c.mag === largest.mag && c.offset < largest.offset)) {
				largest = c
			}
		}
		return aimed ?? largest
	}

	// ==================== Cutout ====================

	/**
	 * Build the cross-section block for a quake: cluster the sequence out of
	 * the local catalog, size and orient the block from it, fly the camera to
	 * it, then drop the terrain lid in once its elevation tiles arrive
	 */
	private async openCutout(quakeId: string): Promise<void> {
		const token = ++this.cutoutToken
		this.cutoutQuakeId = quakeId

		const feature = this.getSceneObject(quakeId) ?? await this.loadSceneObjectById(quakeId)
		if (token !== this.cutoutToken) return
		if (!feature) {
			this.updateCutoutState(quakeId, { status: "error" })
			return
		}
		const main = feature.feature

		// The HUD shows the headline immediately; everything else fills in
		this.updateCutoutState(quakeId, {
			status: "loading",
			quake: {
				mag: main.mag,
				place: main.place,
				eventTime: main.event_time,
				depthKm: main.depth_km,
				lat: main.lat,
				lon: main.lon,
				alert: main.alert,
				tsunami: main.tsunami === 1,
				mmi: main.mmi ?? null
			}
		})

		let sequence: QuakeSequence
		let layout: CutoutLayout
		try {
			sequence = await QuakeSequenceQuery.load(main)
			if (token !== this.cutoutToken) return
			layout = buildLayout(main.mag, sequence.events)
		} catch (error) {
			logger.warn("[EarthquakesDatasetLayer] cutout sequence query failed", error)
			if (token === this.cutoutToken) this.updateCutoutState(quakeId, { status: "error" })
			return
		}

		// Mainshock gate: sequence statistics (Båth, Omori, plane fit, the
		// forecast) only mean something when the selected quake is the
		// largest of its cluster and big enough to have a sequence. An M1.5
		// inside a swarm is a member of ongoing activity, not a mainshock
		let largest: { id: string; mag: number } = { id: main.id, mag: main.mag }
		for (const e of sequence.events) {
			if (e.inSequence && e.mag > largest.mag) largest = { id: e.id, mag: e.mag }
		}
		const isMainshock = largest.id === main.id || largest.mag <= main.mag
		const analysisEnabled = isMainshock && main.mag >= 4

		const node = new QuakeCutoutNode(this.context, sequence, layout, analysisEnabled)
		node.onDismissed = () => this.handleCutoutDismissed(node)
		this.cutout = node
		this.addNode(node)
		this.setMarkersDimmed(main, layout)
		// The video renderer scripts the camera itself and the block is a
		// scene prop there: no focus flight (whose loss would also dismiss
		// the block), the Earth keeps the camera
		if (!isVideoRenderMode()) {
			this.context.focusManager.requestFocus(node)
		}
		this.updateCutoutState(quakeId, {
			status: "ready",
			role: isMainshock ? "mainshock" : "member",
			clusterLargest: isMainshock ? undefined : largest,
			stages: { sequence: true },
			summary: this.summarize(node),
			chart: analysisEnabled ? chartData(sequence, layout) : undefined
		})

		// USGS products for the mainshock: moment tensor (nodal planes) and the
		// official aftershock forecast. The fault-plane sheets wait for this so
		// the fitted plane and the nodal planes appear together
		if (analysisEnabled) {
			void UsgsDetail.load(main.detail)
				.then(detail => {
					if (token !== this.cutoutToken || this.cutout !== node) return
					node.setDetail(detail)
					this.updateCutoutState(quakeId, { stages: { detail: true }, summary: this.summarize(node) })
				})
		} else {
			this.updateCutoutState(quakeId, { stages: { detail: true } })
		}

		// Slab2 interface under the block (if that dataset is synced)
		void Slab2Surface.load(main.lat, main.lon, main.depth_km, layout)
			.then(surface => {
				if (token !== this.cutoutToken || this.cutout !== node) return
				if (surface) node.setSlab(surface)
				this.updateCutoutState(quakeId, { stages: { slab: true }, summary: this.summarize(node) })
			})
			.catch(error => logger.warn("[EarthquakesDatasetLayer] cutout slab query failed", error))

		// Shaking exposure at nearby cities, for mainshocks big enough to be felt widely
		if (analysisEnabled && main.mag >= 5) {
			const strikeDeg = layout.strike * 180 / Math.PI
			void ExposureQuery.load(main.lat, main.lon, main.depth_km, main.mag, strikeDeg)
				.then(exposure => {
					if (token !== this.cutoutToken || this.cutout !== node) return
					node.exposure = exposure
					this.updateCutoutState(quakeId, { stages: { exposure: true }, summary: this.summarize(node) })
				})
				.catch(error => logger.warn("[EarthquakesDatasetLayer] cutout exposure query failed", error))
		} else {
			this.updateCutoutState(quakeId, { stages: { exposure: true } })
		}

		// Historical catalog (if synced): ghosts in the box + facts for the panel
		void QuakeHistoryQuery.load(main.lat, main.lon, layout, new Set(sequence.events.map(e => e.id)))
			.then(history => {
				if (token !== this.cutoutToken || this.cutout !== node) return
				if (history) node.setHistory(history)
				this.updateCutoutState(quakeId, { stages: { history: true }, summary: this.summarize(node) })
			})
			.catch(error => logger.warn("[EarthquakesDatasetLayer] cutout history query failed", error))

		// Terrain: elevation tiles covering the rotated footprint's lat/lon bounds
		let halfEast = 0
		let halfNorth = 0
		for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
			const corner = fromBlockXZ(x * layout.lengthKm / 2, z * layout.widthKm / 2, layout.strike)
			halfEast = Math.max(halfEast, Math.abs(corner.east))
			halfNorth = Math.max(halfNorth, Math.abs(corner.north))
		}
		const centerLat = main.lat + layout.centerNorth / 111.32
		const centerLon = main.lon + layout.centerEast / (111.32 * Math.cos(main.lat * trig.rad))
		try {
			const elevation = await CutoutElevation.load(centerLat, centerLon, halfEast * 1.05, halfNorth * 1.05)
			if (token !== this.cutoutToken || this.cutout !== node) {
				elevation.destroy()
				return
			}
			this.cutoutElevation = elevation
			node.setTerrain(elevation)
			this.updateCutoutState(quakeId, { stages: { terrain: true }, summary: this.summarize(node) })
		} catch (error) {
			logger.warn("[EarthquakesDatasetLayer] cutout elevation failed", error)
			if (token === this.cutoutToken) this.updateCutoutState(quakeId, { stages: { terrain: true } })
		}
	}

	/**
	 * Remove the block. With refocusQuake the camera flies back down to the
	 * quake marker (the user toggled the cutout off); otherwise focus is
	 * released in place
	 */
	private closeCutout(refocusQuake: boolean): void {
		const node = this.cutout
		const quakeId = this.cutoutQuakeId
		this.cutout = undefined
		this.cutoutQuakeId = undefined
		this.cutoutToken++
		this.cutoutElevation?.destroy()
		this.cutoutElevation = undefined
		this.setMarkersDimmed()
		if (!node) return

		node.onDismissed = undefined
		const focusManager = this.context.focusManager
		if (focusManager.getFocusedNode() === node) {
			const feature = refocusQuake && quakeId ? this.getSceneObject(quakeId) : undefined
			if (feature instanceof QuakeFeature) {
				feature.requestFocus()
			} else {
				focusManager.releaseFocus({ preserveCurrentState: true })
			}
		}
		this.removeNode(node)
		node.destroy()
	}

	/** Focus moved elsewhere on its own (user clicked away): drop the block quietly */
	private handleCutoutDismissed(node: QuakeCutoutNode): void {
		if (this.cutout !== node) return
		this.cutout = undefined
		this.cutoutQuakeId = undefined
		this.cutoutToken++
		this.cutoutElevation?.destroy()
		this.cutoutElevation = undefined
		this.setMarkersDimmed()
		this.removeNode(node)
		node.destroy()
		engineBridge.dispatch({ type: engineActions.setQuakeCutout, payload: null })
	}

	/**
	 * Fade the surface domes under an open block (they're drawn as
	 * hypocenters inside it); with no layout, restore every marker
	 */
	private setMarkersDimmed(mainshock?: EarthquakeProperties, layout?: CutoutLayout): void {
		for (const sceneObject of this.sceneObjects.values()) {
			if (!(sceneObject instanceof QuakeFeature)) continue
			let dim = false
			if (mainshock && layout) {
				const q = sceneObject.feature
				const local = localOffsetKm(q.lat, q.lon, mainshock.lat, mainshock.lon)
				const xz = toBlockXZ(local.east - layout.centerEast, local.north - layout.centerNorth, layout.strike)
				dim = insideFootprint(layout, xz.x, xz.z, 5)
			}
			sceneObject.setDimmed(dim)
		}
	}

	private updateCutoutState(quakeId: string, fields: Omit<QuakeCutoutUpdate, "quakeId">): void {
		engineBridge.dispatch({ type: engineActions.updateQuakeCutout, payload: { quakeId, ...fields } })
	}

	/** Panel summary of the block; copies, since redux freezes its payloads and the node keeps filling these in */
	private summarize(node: QuakeCutoutNode): QuakeCutoutSummary {
		return {
			sequenceCount: node.sequence.sequenceCount,
			foreshockCount: node.sequence.foreshockCount,
			largestAftershockMag: node.sequence.largestAftershockMag,
			strikeDeg: Math.round(node.layout.strike / trig.rad),
			lengthKm: Math.round(node.layout.lengthKm),
			depthFloorKm: node.layout.depthFloorKm,
			depthScale: node.layout.depthScale,
			terrainExaggeration: node.terrainExaggeration,
			seaFloorExaggeration: node.seaFloorExaggeration,
			history: node.history ? { ...node.history.facts, ghostCount: node.ghostCount } : undefined,
			analysis: node.analysis ? { ...node.analysis } : undefined,
			slab: node.slabFacts ? { ...node.slabFacts, drawn: node.slabDrawn } : undefined,
			exposure: node.exposure,
			tsunami: node.tsunamiEdgeArrivalMin !== undefined ? { edgeArrivalMin: node.tsunamiEdgeArrivalMin, isochronMin: node.tsunamiIsochronMin } : undefined
		}
	}

	/** Dev debug hook: state of the open cutout */
	public static debugSnapshot(): Record<string, unknown> | null {
		const layer = EarthquakesDatasetLayer.live
		const node = layer?.cutout
		if (!layer || !node) return null
		return {
			quakeId: layer.cutoutQuakeId,
			layout: node.layout,
			sequenceCount: node.sequence.sequenceCount,
			foreshockCount: node.sequence.foreshockCount,
			largestAftershockMag: node.sequence.largestAftershockMag,
			epsKm: node.sequence.epsKm,
			candidateCount: node.sequence.events.length,
			drawnEvents: node.eventCount,
			trend: sequenceTrend(node.sequence.events),
			terrainExaggeration: node.terrainExaggeration,
			seaFloorExaggeration: node.seaFloorExaggeration,
			hasBathymetry: node.hasBathymetry,
			elevationTiles: layer.cutoutElevation?.tileCount,
			bathymetryTiles: layer.cutoutElevation?.bathymetryTileCount,
			history: node.history ? { ...node.history.facts, ghostCount: node.ghostCount } : null,
			analysis: node.analysis,
			slab: node.slabFacts ?? null,
			slabDrawn: node.slabDrawn,
			exposure: node.exposure ?? null,
			tsunamiEdgeArrivalMin: node.tsunamiEdgeArrivalMin,
			focused: layer.context.focusManager.getFocusedNode() === node,
			worldPosition: node.transform.position
		}
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "earthquakes",
			pollIntervalMs: 60_000, // 1 minute
			needsSync: true,
			supportsDelta: true,
			deltaOnlyIngest: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await EarthquakesTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)

		const quakes = this.parseEarthquakeRows(rows)
		await EarthquakesTable.replaceAll(quakes)
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		// Full rolling-window snapshot each publish (delta-only protocol)
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const upserts = this.parseEarthquakeRows(upsertRows)
		await EarthquakesTable.replaceAll(upserts)

		// Deletes
		if (delta.deletes?.path) {
			const deletesUrl = this.resolveUrl(delta.deletes.url, delta.deletes.path)
			const deletesRows = await downloadArrowRows(deletesUrl)
			const deletes = (deletesRows as Array<{ id: string }>).map(r => String(r.id))
			if (deletes.length > 0) {
				await EarthquakesTable.deleteByIds(deletes)
			}
		}
	}

	private parseEarthquakeRows(arrowRows: ArrowRow[]): EarthquakeProperties[] {
		const rows = arrowRows as EarthquakeRow[]
		return rows.map(r => ({
			id: String(r.id),
			place: String(r.place),
			title: String(r.title),
			lon: Number(r.lon),
			lat: Number(r.lat),
			depth_km: Number(r.depth_km),
			mag: Number(r.mag),
			mag_type: r.mag_type ? String(r.mag_type) : null,
			type: String(r.type),
			event_time: String(r.event_time),
			updated: String(r.updated),
			ingested_at: String(r.ingested_at),
			dmin: r.dmin === null || r.dmin === undefined ? null : Number(r.dmin),
			rms: r.rms === null || r.rms === undefined ? null : Number(r.rms),
			gap: r.gap === null || r.gap === undefined ? null : Number(r.gap),
			felt: r.felt === null || r.felt === undefined ? null : Number(r.felt),
			cdi: r.cdi === null || r.cdi === undefined ? null : Number(r.cdi),
			mmi: r.mmi === null || r.mmi === undefined ? null : Number(r.mmi),
			alert: r.alert ? String(r.alert) : null,
			tsunami: Number(r.tsunami ?? 0),
			sig: r.sig === null || r.sig === undefined ? null : Number(r.sig),
			status: String(r.status),
			net: String(r.net),
			code: String(r.code),
			ids: String(r.ids),
			sources: String(r.sources),
			types: String(r.types),
			nst: r.nst === null || r.nst === undefined ? null : Number(r.nst),
			url: String(r.url),
			detail: String(r.detail),
		}))
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(db: Database): Promise<EarthquakeProperties[]> {
		const rows = await db.exec(`
			select
				id,
				place,
				title,
				lon,
				lat,
				depth_km,
				mag,
				mag_type,
				type,
				event_time,
				updated,
				ingested_at,
				dmin,
				rms,
				gap,
				felt,
				cdi,
				mmi,
				alert,
				tsunami,
				sig,
				status,
				net,
				code,
				ids,
				sources,
				types,
				nst,
				url,
				detail
			from earthquakes
			order by event_time desc
			limit 1000
		`) as EarthquakeProperties[]

		return rows
	}

	/**
	 * On-demand load for deep-link focus (?target=quake-...): the rolling
	 * scene-object window keeps only the latest 1000 events (a few days),
	 * but the local table holds the full feed, so an older shared quake is
	 * loadable but never in the window. Pin it so refresh purges don't
	 * remove it out from under the focused camera.
	 */
	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<EarthquakeProperties> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) {
			return existing
		}

		const quake = await EarthquakesTable.getById(id)
		if (!quake) return undefined
		this.pinnedSceneObjects.add(id)
		this.addSceneObject(quake)
		return this.sceneObjects.get(id)
	}

	public createSceneObject(feature: EarthquakeProperties): DatasetSceneObject<EarthquakeProperties> | undefined {
		return new QuakeFeature(this.context, feature)
	}
}
